import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addAddress,
  deleteAddress,
  editAddress,
  fetchAddresses,
} from "../../redux/features/addressSlice";
import { useTable } from "react-table";
import AddressModal from "../../modal/AddressModal";
import { fetchCart } from "../../redux/features/cartSlice";
import { placeYourOrder } from "../../redux/features/placeOrderSlice";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function Checkout() {
  const { t } = useTranslation();
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [gatewayType, setGatewayType] = useState(1);
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { addresses } = useSelector((state) => state.address);
  const { isValid, discount, status, code } = useSelector(
    (state) => state.coupon
  );

  const {
    cartItems,
    totalAmount,
    deliveryCharges,
    couponDiscount,
    netAmount,
    loading,
    error,
  } = useSelector((state) => state.cart);

  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    id: null, // Store the editing address ID
    // address: "",
    // address_title: "",
    city: "",
    state: "",
    country: "",
    email: "",
    house_no: "",
    street: "",
    mobile: "",
    name: "",
    pincode: "",
    // type: "1",
    is_default: "0",
  });

  const resetFormData = () => {
    setFormData({
      id: null, // Store the editing address ID
      // address: "",
      // address_title: "",
      city: "",
      state: "",
      country: "",
      email: "",
      house_no: "",
      street: "",
      mobile: "",
      name: "",
      pincode: "",
      // type: "1",
      is_default: "0",
    });
  };

  // Handle edit action
  const handleEdit = (address) => {
    setShowModal(true);
    // Convert readable text ("Personal" / "Commercial") back to numeric string
    const numericType =
      address.type === "Personal" ? "0" : address.type === "Commercial" ? "1" : "0";

    setFormData({
      ...address,
      type: numericType, // ✅ ensure type is numeric string
      id: address.id,
    });
  };

  const handleDelete = async (address) => {
    if (window.confirm("Are you sure you want to delete this address?")) {
      dispatch(deleteAddress(address.id));
    }
  };

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCart({ code })); // Fetch cart data on first load
  }, [dispatch]);

  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "name" },
      { Header: "Mobile Number", accessor: "mobile" },
      { Header: "House No.", accessor: "house_no" },
      { Header: "Street", accessor: "street" },
      { Header: "City", accessor: "city" },
      { Header: "State", accessor: "state" },
      { Header: "Country", accessor: "country" },
      { Header: "Pincode", accessor: "pincode" },
    ],
    []
  );

  const data = useMemo(() => addresses, [addresses]);

  const { rows, prepareRow } = useTable({ columns, data });

  //handle Modal for add and edit address
  const openModal = () => {
    setShowModal(true);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]:
        type === "checkbox"
          ? checked
            ? 1 // checked = 1
            : 0 // unchecked = 0
          : value, // normal text/select inputs
    }));
  };


  // Handle form submission (Add or Edit)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (formData.id) {
      // Editing existing address
      dispatch(editAddress(formData))
        .then((res) => {
          if (res.type === "address/editAddress/fulfilled") {
            dispatch(fetchAddresses()); // ✅ refresh addresses on success
            resetFormData();            // clear form
            setShowModal(false);        // close modal
          }
        })
        .catch((err) => console.error("Edit failed:", err));
    } else {
      // Adding new address
      dispatch(addAddress(formData))
        .then((res) => {
          if (res.type === "address/addAddress/fulfilled") {
            dispatch(fetchAddresses()); // ✅ refresh addresses on success
            resetFormData();
            setShowModal(false);
          }
        })
        .catch((err) => console.error("Add failed:", err));
    }
  };

  const handleAddressSelect = (id) => {
    setSelectedAddressId(id);
  };

  useEffect(() => {
    if (addresses.length > 0) {
      // Find the default address (is_default can be string or number)
      const defaultAddress = addresses.find(
        (addr) => String(addr.is_default) === "1"
      );

      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else {
        // If no default exists, auto-select the first address (optional)
        setSelectedAddressId(addresses[0].id);
      }
    }
  }, [addresses]);

  const handleRadioChange = (event) => {
    setGatewayType(Number(event.target.value));
  };

  const handleSubmit = (e) => {
    if (!selectedAddressId) {
      toast.error(`${t("Please_select_your_address_before_proceeding")}.`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000, // Auto close after 2 seconds
      });
    }
    e.preventDefault();

    const orderData = {
      billing_address_id: selectedAddressId,
      coupon_code: code,
      gateway_type: gatewayType,
      shipping_address_id: selectedAddressId,
    };

    // console.log("Order Data:", orderData);

    dispatch(placeYourOrder(orderData))
      .unwrap()
      .then((orders) => {
        if (!orders || !Array.isArray(orders)) {
          console.error("Error: Unexpected API response format", orders);
          return;
        }

        // console.log("Order Placed Successfully:", orders);
        const orderIds = orders.map((order) => order.order_id).join(",");
        if (gatewayType === 1) {
          // Extract all order_id values

          if (orderIds) {
            const paypalUrl = `${process.env.REACT_APP_API_URL}user/paypal-pay/${orderIds}`;
            window.open(paypalUrl, "_blank"); // Opens the PayPal link in a new tab
          } else {
            console.error("Error: No valid order IDs found.");
          }
        } else if (gatewayType === 2) {
          navigate(`/pay-stripe/${orderIds}`);
        } else if (gatewayType === 3) {
          navigate(`/pay-klarna/${orderIds}`);
        }
      })
      .catch((error) => {
        console.error("Order placement failed:", error);
      });
  };

  return (
    <>
      <section className="checkout-section pt-3 pb-80 main-bg">
        <div className="container">
          <div className="row">
            <div className="col-xl-12">
              <div
                className="checkout-wrapper aos-init aos-animate row"
                data-aos="fade-up"
                data-aos-duration="1200"
              >
                <div className="col-xl-4">
                  <div className="row">
                    {rows.map((row) => {
                      prepareRow(row);
                      const data = row.original;

                      return (
                        <div className="col-12 mb-2" key={data.id || Math.random()}>
                          <div
                            className={`card shadow-sm border-0 rounded-4 address-card position-relative ${selectedAddressId === data.id ? "border-success border-2" : ""
                              }`}
                            onClick={() => handleAddressSelect(data.id)}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              background:
                                selectedAddressId === data.id
                                  ? "var(--secondary-light-color)"
                                  : "#fff",
                            }}
                          >
                            {/* Action Buttons */}
                            <div className="position-absolute top-0 end-0 m-3 d-flex gap-2">
                              <button
                                className="btn btn-light btn-sm border rounded-circle shadow-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(data);
                                }}
                              >
                                <i className="fas fa-edit text-success"></i>
                              </button>
                              <button
                                className="btn btn-light btn-sm border rounded-circle shadow-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(data);
                                }}
                              >
                                <i className="fas fa-trash text-danger"></i>
                              </button>
                            </div>

                            {/* Address Info */}
                            <div className="card-body p-4 pb-2">
                              <div className="d-flex align-items-center justify-content-between pt-4 mt-2">
                                <h5 className="fw-bold mb-0" style={{
                                  color:
                                    selectedAddressId === data.id
                                      ? "#fff"
                                      : "var(--secondary-light-color)",
                                }}>
                                  {data.name || "Unnamed Address"}
                                </h5>
                                <div className="badge-container">
                                  {/* <span
                                    className="address-badge"
                                    style={{
                                      background: selectedAddressId === data.id ? "#fff" : "rgba(0, 80, 98, 0.1)",
                                      color: "var(--secondary-light-color)",
                                    }}
                                  >
                                    {data.type || "N/A"}
                                  </span> */}

                                  {data.is_default === 1 && (
                                    <span
                                      className="address-badge"
                                      style={{
                                        background: selectedAddressId === data.id ? "#fff" : "rgba(0, 80, 98, 0.1)",
                                        color: "var(--secondary-light-color)",
                                      }}
                                    >
                                      Default
                                    </span>
                                  )}
                                </div>

                              </div>

                              <div className="address-details">
                                <div className="mb-1">
                                  <label className="small mb-1"
                                    style={{
                                      color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                    }}>
                                    Mobile Number
                                  </label>
                                  <div className="form-control border-0 bg-light fw-semibold">
                                    {data.mobile || "-"}
                                  </div>
                                </div>

                                {/* FIXED ADDRESS FIELD */}
                                <div className="mb-1">
                                  <label className="small mb-1"
                                    style={{
                                      color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                    }}>
                                    Address
                                  </label>

                                  <div className="form-control border-0 bg-light">
                                    {[
                                      data.house_no,
                                      data.street,
                                      data.city,
                                      data.state,
                                      data.country,
                                      data.pincode
                                    ].filter(Boolean).join(", ") || "-"}
                                  </div>
                                </div>

                                <div className="row">
                                  <div className="col-md-6 mb-1">
                                    <label className="small mb-1"
                                      style={{
                                        color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                      }}>
                                      City
                                    </label>
                                    <div className="form-control border-0 bg-light">
                                      {data.city || "-"}
                                    </div>
                                  </div>

                                  <div className="col-md-6 mb-1">
                                    <label className="small mb-1"
                                      style={{
                                        color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                      }}>
                                      State
                                    </label>
                                    <div className="form-control border-0 bg-light">
                                      {data.state || "-"}
                                    </div>
                                  </div>

                                  <div className="col-md-6 mb-1">
                                    <label className="small mb-1"
                                      style={{
                                        color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                      }}>
                                      Country
                                    </label>
                                    <div className="form-control border-0 bg-light">
                                      {data.country || "-"}
                                    </div>
                                  </div>

                                  <div className="col-md-6 mb-2">
                                    <label className="small mb-1"
                                      style={{
                                        color: selectedAddressId === data.id ? "#fff" : "var(--secondary-light-color)",
                                      }}>
                                      Pincode
                                    </label>
                                    <div className="form-control border-0 bg-light">
                                      {data.pincode || "-"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add New Button */}
                  <div className="text-start">
                    <button
                      className="theme-btn style-one shadow-sm fw-semibold"
                      onClick={openModal}
                    >
                      <i className="fas fa-plus me-2"></i> {t("Add_New_Address")}
                    </button>
                  </div>
                </div>

                <div className="col-xl-8">
                  <div className="row px-3 mt-4 mt-xl-0">
                    <div className="card p-4 bg-white">
                      <div className="order-summary-wrapper mb-30">
                        <h3 className="title">{t("Billing_details")}</h3>
                        <div className="order-list">
                          <div className="list-item">
                            <div className="item-title">{t("Product")}</div>
                            <div className="subtotal">{t("Subtotal")}</div>
                          </div>

                          {/* Dynamically render cart items */}
                          {cartItems.length > 0 ? (
                            cartItems.map((item) => (
                              <>
                                <div className="product-item" key={item.id}>
                                  <div className="product-name">
                                    {item.title} <span>x{item.quantity}</span>
                                    <br />
                                    {item.combinations
                                      ?.filter((combo) => combo.key !== "1") // Exclude key "1"
                                      .map((combo, index) => (
                                        <div key={index}>
                                          <b>{combo.key}:</b>{" "}
                                          <span className="me-2">
                                            {combo.value}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                  <div className="product-total">
                                    €{item.sales_price * item.quantity}
                                  </div>
                                </div>
                                <div>
                                  <b>{t("Estimated_Delivery_on")} :</b>{" "}
                                  {new Date(
                                    Date.now() +
                                    (item?.delivered_day || 2) * 86400000
                                  ).toLocaleDateString("en-US", {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </div>
                              </>
                            ))
                          ) : (
                            <p>{t("No_items_in_the_cart")}</p>
                          )}

                          {/* Subtotal */}
                          <div className="list-item">
                            <div className="subtotal">{t("Subtotal")}</div>
                            <div className="product-total">
                              €{totalAmount.toFixed(2)}
                            </div>
                          </div>

                          {/* Shipping Charges */}
                          <div className="list-item">
                            <div className="shipping">{t("Shipping")}</div>
                            <div className="shipping-total">
                              {deliveryCharges > 0
                                ? `€${deliveryCharges.toFixed(2)}`
                                : "Free"}
                            </div>
                          </div>

                          {/* Coupon Discount */}
                          {couponDiscount > 0 && (
                            <div className="list-item">
                              <div className="discount">{t("Discount")}</div>
                              <div className="discount-total">
                                -€{couponDiscount}
                              </div>
                            </div>
                          )}

                          {/* Total */}
                          <div className="list-item">
                            <div className="total">{t("Total")}</div>
                            <div className="product-total">
                              €{netAmount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="payment-method-wrapper">
                          <h3 className="title mb-20 mt-3">{t("Payments_Method")}</h3>

                          {/* Coupon Code Input */}
                          {/* <div className="mb-3">
                        <label htmlFor="couponCode" className="form-label">
                          Coupon Code (Optional)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="couponCode"
                          name="couponCode"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          placeholder="Enter coupon code"
                        />
                      </div> */}

                          {/* Payment Methods */}
                          <ul id="paymentMethod" className="space-y-4">
                            {[
                              {
                                id: 1,
                                name: "PayPal",
                                logo: "https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg",
                              },
                              {
                                id: 2,
                                name: "Stripe",
                                logo: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg",
                              },
                              {
                                id: 3,
                                name: "Klarna",
                                logo: "https://upload.wikimedia.org/wikipedia/commons/9/9a/Klarna_Logo_black.svg",
                              },
                            ].map((method) => (
                              <li key={method.id} className="mb-0 py-2">
                                <label
                                  htmlFor={`method${method.id}`}
                                  className={`d-flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-all bg-white shadow-sm hover:shadow-md hover:border-emerald-500 ${gatewayType === method.id
                                    ? "border-emerald-500 ring-1 ring-emerald-300"
                                    : "border-gray-200"
                                    }`}
                                >
                                  {/* Radio on left */}
                                  <input
                                    type="radio"
                                    name="paymentMethod"
                                    id={`method${method.id}`}
                                    value={method.id}
                                    checked={gatewayType === method.id}
                                    onChange={handleRadioChange}
                                    className="accent-emerald-500 w-5 h-5"
                                  />

                                  {/* Logo + Text */}
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={method.logo}
                                      alt={method.name}
                                      style={{ height: '25px' }}
                                      className="h-6 object-contain"
                                    />
                                  </div>
                                </label>
                              </li>
                            ))}
                          </ul>


                          {/* Submit Button */}
                          <button type="submit" className="theme-btn style-one my-3">
                            {t("Place_Order")}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >

      <AddressModal
        showModal={showModal}
        setShowModal={setShowModal}
        formData={formData}
        handleChange={handleChange}
        handleFormSubmit={handleFormSubmit}
        resetFormData={resetFormData}
      />
    </>
  );
}

export default Checkout;
